import * as React from 'react'
import { createPortal } from 'react-dom'
import {
    arrow,
    autoUpdate,
    flip,
    offset as offsetMiddleware,
    shift,
    useFloating,
    type Placement,
} from '@floating-ui/react-dom'
import classNames from 'classnames'

/**
 * Drop-in replacement for `Popover2` from `@blueprintjs/popover2`.
 *
 * Goal: keep the Blueprint API surface so callsites can switch by changing
 * the import. Visuals are hand-tuned to match Blueprint's popover2.
 *
 * Audit-supported props (counts from the 2026-05-07 sweep audit):
 *   content                45 — popover body, JSX
 *   interactionKind        36 — 'hover' | 'click' (default 'click', matches Popover2)
 *   position               23 — string OR `Position.X` enum value
 *   popoverClassName        9 — most callsites pass `Classes.POPOVER2_CONTENT_SIZING`
 *   disabled                8 — never opens
 *   className               8 — wrapper className (e.g. dashed underline trigger)
 *   onClose                 7 — fires when the popover wants to close
 *   isOpen                  7 — controlled mode
 *   onOpening               4 — fires before open transition (used to lazy-fetch)
 *   openOnTargetFocus       3 — currently always {false}
 *   minimal                 3 — drop arrow + lighter shadow
 *   style                   2 — wrapper inline style
 *   portalContainer         1 — portal target element
 *   targetTagName           1 — wrapper element tag (default 'span')
 *   onOpened                1
 *   modifiers               1 — only the navbar-avatar callsite, only `offset` modifier
 *   placement               1 — alias for `position` (Blueprint accepts both)
 *
 * Differences from Tooltip:
 *   - Default interactionKind is 'click' (not 'hover-target') to match Popover2's default.
 *   - Supports controlled mode via `isOpen` + `onClose`.
 *   - `minimal` skips the arrow and uses a lighter elevation.
 *   - Translates `modifiers.offset.options.offset = [skidding, distance]`
 *     into floating-ui's `offset({ mainAxis, crossAxis })`.
 *   - Adds Escape-to-close for `interactionKind === 'click'` (popovers often
 *     contain forms; users expect Escape to dismiss).
 *
 * Not supported (no callsite uses these as of audit): `target` prop (all
 * callsites pass the trigger as children), arbitrary popper.js modifiers
 * besides `offset`, `boundary`, `usePortal={false}`, `inheritDarkTheme`.
 */

type PopoverPosition =
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-left'
    | 'top-right'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-left'
    | 'bottom-right'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-top'
    | 'left-bottom'
    | 'right'
    | 'right-top'
    | 'right-bottom'

type InteractionKind = 'hover' | 'hover-target' | 'click'

interface PopoverModifiers {
    offset?: {
        enabled?: boolean
        options?: {
            offset?: [number, number]
        }
    }
    // Other popper.js modifiers are ignored with a console.warn — no callsite
    // uses them as of the audit.
    [other: string]: unknown
}

export interface PopoverProps {
    content: React.ReactNode
    children: React.ReactNode
    interactionKind?: InteractionKind
    position?: PopoverPosition
    /** Alias for `position`. Blueprint accepts both. */
    placement?: PopoverPosition
    disabled?: boolean
    /** Controlled-mode current open state. Pair with `onClose`. */
    isOpen?: boolean
    /** Uncontrolled-mode initial open state. */
    defaultIsOpen?: boolean
    onClose?: () => void
    onOpening?: () => void
    onOpened?: () => void
    openOnTargetFocus?: boolean
    minimal?: boolean
    popoverClassName?: string
    className?: string
    style?: React.CSSProperties
    targetTagName?: keyof JSX.IntrinsicElements
    portalContainer?: HTMLElement
    modifiers?: PopoverModifiers
    hoverOpenDelay?: number
    hoverCloseDelay?: number
}

const positionToPlacement: Record<PopoverPosition, Placement> = {
    auto: 'bottom',
    'auto-start': 'bottom-start',
    'auto-end': 'bottom-end',
    top: 'top',
    'top-left': 'top-start',
    'top-right': 'top-end',
    'top-start': 'top-start',
    'top-end': 'top-end',
    bottom: 'bottom',
    'bottom-left': 'bottom-start',
    'bottom-right': 'bottom-end',
    'bottom-start': 'bottom-start',
    'bottom-end': 'bottom-end',
    left: 'left',
    'left-top': 'left-start',
    'left-bottom': 'left-end',
    right: 'right',
    'right-top': 'right-start',
    'right-bottom': 'right-end',
}

/**
 * Re-exported so callsites can drop the `import { Classes } from
 * '@blueprintjs/popover2'` line and import from this module instead. The
 * value is identical to Blueprint's so existing CSS keeps working until we
 * own the styling end-to-end.
 *
 * `POPOVER_DISMISS` is the class Blueprint's MenuItem auto-applies and
 * Popover2 watches for to auto-close on click. Our wrapper does the same
 * (see the "dismiss-on-click inside content" effect below). Re-exported
 * here so callsites that want to dismiss the popover from a non-MenuItem
 * element (a custom button inside the content) can opt in by adding the
 * class.
 */
export const Classes = {
    POPOVER2_CONTENT_SIZING: 'bp4-popover2-content-sizing',
    POPOVER_DISMISS: 'bp4-popover-dismiss',
} as const

const Popover = React.forwardRef<HTMLElement, PopoverProps>(function Popover(
    {
        content,
        children,
        interactionKind = 'click',
        position,
        placement: placementProp,
        disabled = false,
        isOpen: controlledOpen,
        defaultIsOpen = false,
        onClose,
        onOpening,
        onOpened,
        openOnTargetFocus = true,
        minimal = false,
        popoverClassName,
        className,
        style,
        targetTagName = 'span',
        portalContainer,
        modifiers,
        hoverOpenDelay = 100,
        hoverCloseDelay = 300,
    },
    forwardedRef,
) {
    const isControlled = controlledOpen !== undefined
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultIsOpen)
    const open = isControlled ? !!controlledOpen : uncontrolledOpen

    const arrowRef = React.useRef<HTMLDivElement>(null)
    const openTimer = React.useRef<number | null>(null)
    const closeTimer = React.useRef<number | null>(null)
    const isMountedRef = React.useRef(true)

    // Resolve placement from either `position` or `placement` (Blueprint
    // accepts both). Default depends on interactionKind: hover popovers
    // typically come up below the trigger, click ones too — Blueprint's
    // default is 'auto' which resolves to 'bottom'.
    const requestedPosition: PopoverPosition = position ?? placementProp ?? 'auto'
    const basePlacement: Placement = positionToPlacement[requestedPosition] ?? 'bottom'

    // Translate Blueprint's modifiers.offset to floating-ui's offset middleware.
    // Blueprint/popper.js: `offset = [skidding, distance]` (skidding = along
    // main axis, distance = perpendicular). floating-ui: `mainAxis` is the
    // distance from the trigger, `crossAxis` is the alignment shift. Map:
    //   distance -> mainAxis
    //   skidding -> crossAxis
    let resolvedOffset: number | { mainAxis: number; crossAxis: number } = 8
    const offsetSpec = modifiers?.offset
    if (offsetSpec && offsetSpec.enabled !== false && Array.isArray(offsetSpec.options?.offset)) {
        const [skidding, distance] = offsetSpec.options!.offset!
        resolvedOffset = { mainAxis: distance ?? 8, crossAxis: skidding ?? 0 }
    }
    if (modifiers) {
        const unsupported = Object.keys(modifiers).filter((k) => k !== 'offset')
        if (unsupported.length > 0 && process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(
                `[ui/popover] Ignoring unsupported popper.js modifier(s): ${unsupported.join(', ')}. ` +
                    `Only \`offset\` is translated to floating-ui. Add to popover.tsx if you need them.`,
            )
        }
    }

    const {
        refs,
        floatingStyles,
        middlewareData,
        placement: actualPlacement,
    } = useFloating({
        placement: basePlacement,
        open,
        // We never let floating-ui drive open state — we own it. But it still
        // wants this callback for some internal lifecycle.
        onOpenChange: () => {
            /* noop; we control open/close */
        },
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
        middleware: [
            offsetMiddleware(resolvedOffset),
            flip({ fallbackAxisSideDirection: 'start' }),
            shift({ padding: 8 }),
            arrow({ element: arrowRef }),
        ],
    })

    React.useImperativeHandle(forwardedRef, () => refs.reference.current as HTMLElement)

    const clearTimers = () => {
        if (openTimer.current) {
            window.clearTimeout(openTimer.current)
            openTimer.current = null
        }
        if (closeTimer.current) {
            window.clearTimeout(closeTimer.current)
            closeTimer.current = null
        }
    }

    const requestOpen = () => {
        if (!isControlled) {
            setUncontrolledOpen(true)
        }
        // In controlled mode the parent has to flip isOpen itself; we still
        // fire onOpened so they have a hook for side effects.
        onOpened?.()
    }

    const requestClose = () => {
        if (!isControlled) {
            setUncontrolledOpen(false)
        }
        onClose?.()
    }

    const scheduleOpen = () => {
        if (disabled) return
        clearTimers()
        onOpening?.()
        openTimer.current = window.setTimeout(
            () => {
                if (!isMountedRef.current) return
                requestOpen()
            },
            Math.max(0, hoverOpenDelay),
        )
    }

    const scheduleClose = () => {
        clearTimers()
        closeTimer.current = window.setTimeout(
            () => {
                if (!isMountedRef.current) return
                requestClose()
            },
            Math.max(0, hoverCloseDelay),
        )
    }

    const toggleClick = () => {
        if (disabled) return
        if (open) {
            requestClose()
        } else {
            onOpening?.()
            requestOpen()
        }
    }

    React.useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
            clearTimers()
        }
    }, [])

    // Click-away handler when interactionKind === 'click'. Also fires for
    // controlled mode — parent's onClose still gets called so they can flip
    // their own state.
    //
    // Use `click` (not `mousedown`) so that any onClick handler on the
    // clicked element fires before we close. This matters for nested
    // popovers: a submenu's content is portaled to body, so it's NOT inside
    // this popover's floating element. If we closed on mousedown, the
    // submenu would unmount before its MenuItem's onClick had a chance to
    // run (e.g. selecting a saved filter from the Saved Filters submenu).
    // Closing on click lets the inner handler run first; React then
    // unmounts on the next commit.
    React.useEffect(() => {
        if (interactionKind !== 'click' || !open) return
        // A control outside the trigger can open the popover in its own
        // click handler (a filter tag does). That click still bubbles when
        // this effect runs, so the listener below gets it as an outside
        // click and closes the popover in the click that opened it.
        // `window.event` is the event in dispatch now, if there is one.
        const openingEvent = window.event
        const onDocClick = (event: MouseEvent) => {
            if (event === openingEvent) return
            const refEl = refs.reference.current as HTMLElement | null
            const floatEl = refs.floating.current
            const t = event.target as Node
            if (refEl?.contains(t) || floatEl?.contains(t)) return
            requestClose()
        }
        document.addEventListener('click', onDocClick)
        return () => document.removeEventListener('click', onDocClick)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interactionKind, open])

    // Dismiss-on-click handler. When a click inside the popover content lands
    // on (or descends from) an element with the `bp4-popover-dismiss` class,
    // close the popover. This is how Blueprint's MenuItem auto-closes its
    // surrounding action menu — every callsite using EllipsisMenu / our Menu
    // depends on it (clicking "Give coins" should make the menu go away).
    //
    // Runs regardless of interactionKind because hover popovers can still
    // contain dismiss-bearing buttons (e.g. a submenu's leaf items, where
    // clicking dismisses both the submenu and its parent click-popover).
    //
    // Use `click` rather than `mousedown` so the item's own onClick handler
    // (which fires on click) runs first — closing on mousedown would unmount
    // the content before React dispatches the synthetic click.
    React.useEffect(() => {
        if (!open) return
        const onDocClick = (event: MouseEvent) => {
            const floatEl = refs.floating.current
            const target = event.target as Element | null
            if (!target || !floatEl?.contains(target)) return
            // closest() walks up from the click target, so this catches both
            // a direct click on a `.bp4-popover-dismiss` element and a click
            // on a child of one (e.g. the icon inside a MenuItem's anchor).
            if (target.closest?.('.bp4-popover-dismiss')) {
                requestClose()
            }
        }
        document.addEventListener('click', onDocClick)
        return () => document.removeEventListener('click', onDocClick)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // Escape-to-close. Popovers often contain forms; users expect Escape to
    // dismiss. Tooltip doesn't need this, Popover does.
    React.useEffect(() => {
        if (interactionKind !== 'click' || !open) return
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                requestClose()
            }
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interactionKind, open])

    const triggerHandlers: React.HTMLAttributes<HTMLElement> = {}
    if (interactionKind === 'click') {
        triggerHandlers.onClick = toggleClick
    } else {
        triggerHandlers.onMouseEnter = scheduleOpen
        triggerHandlers.onMouseLeave = scheduleClose
        if (openOnTargetFocus) {
            triggerHandlers.onFocus = scheduleOpen
            triggerHandlers.onBlur = scheduleClose
        }
    }

    // Keep the popover itself open while the cursor is over it (interactionKind
    // 'hover' but not 'hover-target'). Popover2's default is 'click' so this is
    // less common than for Tooltip2, but a few callsites use 'hover' explicitly.
    const popoverHoverHandlers: React.HTMLAttributes<HTMLElement> = {}
    if (interactionKind === 'hover') {
        popoverHoverHandlers.onMouseEnter = clearTimers
        popoverHoverHandlers.onMouseLeave = scheduleClose
    }

    const Wrapper = targetTagName as React.ElementType
    const arrowSide: 'top' | 'right' | 'bottom' | 'left' =
        (actualPlacement.split('-')[0] as 'top' | 'right' | 'bottom' | 'left') || 'bottom'
    const arrowOpposite = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' } as const
    const arrowX = middlewareData.arrow?.x
    const arrowY = middlewareData.arrow?.y

    // Translate Classes.POPOVER2_CONTENT_SIZING (the only popoverClassName
    // pattern in the audit beyond a single custom class) into Tailwind. Same
    // approach as Tooltip — Blueprint applies these via a nested selector
    // that won't match our DOM, so we emit the equivalent utilities ourselves.
    const hasContentSizing =
        typeof popoverClassName === 'string' && popoverClassName.includes('popover2-content-sizing')

    const hasContent = content != null && content !== false && content !== ''
    // We use `filter: drop-shadow(...)` instead of `box-shadow` so the shadow
    // follows the silhouette of body + arrow as one shape. With box-shadow,
    // each child renders its own outline and the arrow ends up as a fully
    // outlined diamond instead of a triangle merging into the popover body.
    // Drop-shadow stack approximates Blueprint's three-layer popover shadow:
    //   0 0 0 1px rgba(17,20,24,0.1)              — hairline border
    //   0 2px 4px rgba(17,20,24,0.2)              — close shadow
    //   0 8px 24px rgba(17,20,24,0.2)             — drop shadow
    const popoverNode =
        open && !disabled && hasContent ? (
            <div
                ref={refs.setFloating}
                style={{
                    ...floatingStyles,
                    zIndex: 21,
                    filter: minimal
                        ? 'drop-shadow(0 0 0.5px rgba(17,20,24,0.2)) drop-shadow(0 2px 4px rgba(17,20,24,0.1))'
                        : 'drop-shadow(0 0 0.5px rgba(17,20,24,0.2)) drop-shadow(0 2px 4px rgba(17,20,24,0.2)) drop-shadow(0 8px 24px rgba(17,20,24,0.2))',
                }}
                className={classNames(
                    'pointer-events-auto rounded-[3px] bg-white text-[14px] text-[#1c2127]',
                    hasContentSizing ? 'max-w-[350px] p-[20px]' : null,
                    popoverClassName,
                )}
                role="dialog"
                {...popoverHoverHandlers}
            >
                {content}
                {!minimal && (
                    <div
                        ref={arrowRef}
                        className="absolute h-[14px] w-[14px] rotate-45 bg-white"
                        style={{
                            left: arrowX != null ? `${arrowX}px` : undefined,
                            top: arrowY != null ? `${arrowY}px` : undefined,
                            // Half the arrow's diagonal (10px) sticks out; the
                            // other half overlaps the popover body and hides the
                            // base side seam. drop-shadow on the parent traces
                            // the whole silhouette.
                            [arrowOpposite[arrowSide]]: '-5px',
                        }}
                    />
                )}
            </div>
        ) : null

    const portalTarget = portalContainer ?? (typeof document !== 'undefined' ? document.body : null)

    return (
        <>
            <Wrapper
                ref={refs.setReference as React.Ref<HTMLElement>}
                className={className}
                style={style}
                {...triggerHandlers}
            >
                {children}
            </Wrapper>
            {popoverNode && portalTarget ? createPortal(popoverNode, portalTarget) : popoverNode}
        </>
    )
})

export { Popover }
